import * as intents from "../data/intents";
import { newActionId } from "../data/callables";
import type { SeasonModel } from "../shared/types/contracts";
import type { EnqueueCommand, RunCommand } from "./commandTypes";
import type { TeamCommands } from "./teamTypes";
import { actionKeys } from "./actionKeys";
import { eventRecovery } from "./useSerializedGameQueue";

interface SeasonCommandOptions {
  teamId: string;
  seasons: SeasonModel[];
  activeSeasonId: string | null;
  run: RunCommand;
  enqueue: EnqueueCommand;
  showToast: (message: string) => void;
}

export type SeasonCommands = Pick<
  TeamCommands,
  "startSeason" | "closeSeason" | "reopenSeason"
>;

export function useSeasonCommands({
  teamId,
  seasons,
  activeSeasonId,
  run,
  enqueue,
  showToast,
}: SeasonCommandOptions): SeasonCommands {
  const startSeason = (year: number): Promise<boolean> => {
    const actionId = newActionId();
    return enqueue(
      () =>
        run(
          () => intents.sendStartSeason({ teamId, year, actionId }),
          () => {
            showToast(`${year} season started`);
          },
        ),
      {
        key: actionKeys.seasonStart,
        chainKey: "season",
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };
  const closeSeason = (): Promise<boolean> => {
    if (!activeSeasonId) return Promise.resolve(false);
    const actionId = newActionId();
    return enqueue(
      () =>
        run(
          () =>
            intents.sendCloseSeason({
              teamId,
              seasonId: activeSeasonId,
              actionId,
            }),
          () => {
            const label =
              seasons.find((season) => season.id === activeSeasonId)?.label ??
              "Season";
            showToast(`${label} closed`);
          },
        ),
      {
        key: actionKeys.seasonClose,
        chainKey: "season",
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };
  const reopenSeason = (seasonId: string): Promise<boolean> => {
    const actionId = newActionId();
    return enqueue(
      () =>
        run(
          () => intents.sendReopenSeason({ teamId, seasonId, actionId }),
          () => {
            showToast("Season reopened");
          },
        ),
      {
        key: actionKeys.seasonReopen(seasonId),
        chainKey: "season",
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };

  return { startSeason, closeSeason, reopenSeason };
}
